<template>
  <model-edition-form
    ref="commentForm"
    v-model="comment"
    :model-name="t('task.comment')"
    base-url="/api/taskcomments"
    :model-id="modelId"
    :mode="mode"
    :with-title="false"
    save-wording="Comment"
    :can-close="false"
    @close="emit('close')"
    @model:updated="commentUpdated"
    @model:created="commentCreated"
    @model:loaded="fixTaskId"
  >
    <rich-text-editor
      v-if="comment"
      v-model="comment.comment"
      :label="t('task.comments')"
      :hint="t('task.comment_hint')"
      :error="!!validationErrors.comment"
      :error-messages="validationErrors.comment"
    />

    <template #actions>
      <v-menu :open-on-hover="!mobile">
        <template #activator="{ props: activatorProps }">
          <v-btn
            color="primary"
            size="default"
            v-bind="activatorProps"
            :text="t('task.comment_and_advance')"
          />
        </template>

        <v-list>
          <v-list-item
            v-for="(item, index) in advanceStates"
            :key="index"
            @click="addCommentAndAdvance(item)"
          >
            <v-list-item-title class="text-body-large">
              {{ item.title }}
            </v-list-item-title>
          </v-list-item>
        </v-list>
      </v-menu>
    </template>
  </model-edition-form>
</template>

<script setup lang="ts">
import type { AxiosError } from 'axios';
import axios from 'axios';
import { storeToRefs } from 'pinia';
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useDisplay } from 'vuetify';
import RichTextEditor from '@/components/rich-text/RichTextEditor.vue';
import { useErrorStore } from '@/stores/errors';
import ModelEditionForm from '../ModelEditionForm.vue';

const props = defineProps<{
  modelId?: number;
  mode: 'edit' | 'create' | 'show';
  task: App.Models.Task;
}>();
const emit = defineEmits(['model:updated', 'model:created', 'model:loaded', 'error:handled', 'close']);
const { mobile } = useDisplay();
const errorStore = useErrorStore();
const { validationErrors } = storeToRefs(errorStore);
const { t } = useI18n();
const { showSnackMessage } = useErrorStore();
const commentForm = ref<typeof ModelEditionForm>();
const comment = ref<Partial<App.Models.TaskComment>>();

if (!comment.value) {
  comment.value = {
    id: -1,
    task_id: props.task.id,
    comment: '',
  };
}
const taskStates = ref<any>(null);

// Fixes Cached Comment Task ID
function fixTaskId() {
  if (comment.value) {
    comment.value.task_id = props.task.id;
  }
}
const advanceStates = computed(() => taskStates.value?.filter((state: any) => state.value !== 'new' && state.value !== props.task.status));
async function loadTaskStates() {
  const response = await axios.get('/api/tasks/states');
  taskStates.value = response.data.data.status.map((status: string) => ({
    value: status,
    title: t(`states.${status}`),
  }));
}

async function addCommentAndAdvance(statusCode) {
  try {
    commentForm.value?.save();
    const newTask = props.task;
    newTask.status = statusCode.value;
    await axios.put(`/api/tasks/${props.task.id}`, newTask);
    comment.value.comment = '';
    emit('model:updated');
  }
  catch (error) {
    const errors = error as Error | AxiosError;
    if (axios.isAxiosError(errors)) {
      if (errors.response?.status === 422) {
        validationErrors.value = errors.response.data.errors;
      }
    }
    showSnackMessage(t('task.cannot_update'));
  }
}

if (!taskStates.value) {
  loadTaskStates();
}

function commentUpdated() {
  emit('model:updated');
  comment.value.comment = '';
}

function commentCreated() {
  emit('model:created');
  comment.value.comment = '';
}

watch(comment, () => {
  commentForm.value?.validate();
}, { deep: true });
</script>
